import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGenerationPhase, type GenerationPhase } from "./useGenerationPhase";
import type { ImageCandidate } from "../lib/api";

type Result = { candidate: ImageCandidate };

interface UseImageGeneration {
  generate: (postId: string, prompt: string, idempotencyKey: string) => void;
  phase: GenerationPhase;
  result: Result | null;
  error: string | null;
  reset: () => void;
}

export const useImageGeneration = (): UseImageGeneration => {
  const qc = useQueryClient();
  const [currentPostId, setCurrentPostId] = useState<string | null>(null);

  const { start, phase, result, error, reset } = useGenerationPhase<Result>({
    action: "generate-image",
    parseResult: (msg) => ({ candidate: msg["candidate"] as ImageCandidate }),
  });

  // ao chegar em "done", invalida o draft pra react-query refetch
  useEffect(() => {
    if (phase === "done" && currentPostId) {
      qc.invalidateQueries({ queryKey: ["draft", currentPostId] });
    }
  }, [phase, currentPostId, qc]);

  const generate = useCallback(
    (postId: string, prompt: string, idempotencyKey: string) => {
      setCurrentPostId(postId);
      start({ postId, prompt, idempotencyKey });
    },
    [start]
  );

  return { generate, phase, result, error, reset };
};
